import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const atmosphereGradientFieldSkill: Skill = {
    name: SKILL_NAMES.atmosphereGradientField,
    kind: 'codeo',
    description:
        'Build a premium, alive backdrop from a few large blurred colour orbs that drift and ' +
        'breathe behind the content — the soft-gradient atmosphere that separates flat-colour ' +
        'scenes from ones that feel produced.',
    instructions: `# Atmosphere Gradient Field

A flat background colour is the fastest way to read as a template. The fix is a *field*: two to four large, heavily-blurred radial-gradient orbs in the palette's colours, sitting behind everything, drifting slowly past each other so the background is never static. It reads as depth and light, not as decoration the viewer notices.

## Markup

Orbs are plain \`<div>\`s, placed first in the DOM so they paint behind content. Size each one to **cover the whole canvas** (≥ its full width and height): the radial gradient's fade, not the box size, controls how large the visible glow reads, so on a canvas-sized box the glow is a soft blob anchored wherever you place the gradient's centre — you move and resize the glow by moving the gradient's centre and pulling its stop in, never by shrinking the box. (A canvas-covering box also reads as a full-bleed background layer to the canvas-bounds lint.) Keep them softly blurred and low-opacity.

\`\`\`html
<div class="atmo-orb" id="atmo-a"></div>
<div class="atmo-orb" id="atmo-b"></div>
<div class="atmo-orb" id="atmo-c"></div>
\`\`\`

\`\`\`css
.atmo-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(90px);          /* the blur is what sells it — don't skimp */
    opacity: 0.55;
    z-index: 0;                  /* behind every content layer */
    /* NO transform here — GSAP owns x/y/scale on these elements */
}
/* Each box covers the canvas; the gradient's transparent stop sets how big the visible
   glow reads — smaller % = tighter glow. */
#atmo-a { width: 1900px; height: 1900px; left: -550px; top: -530px;
          background: radial-gradient(circle, var(--l5-color-1, #7a6dff) 0%, rgba(122,109,255,0) 55%); }
#atmo-b { width: 2000px; height: 2000px; left: 520px; top: -360px;
          background: radial-gradient(circle, var(--l5-color-2, #2bd4c0) 0%, rgba(43,212,192,0) 45%); }
#atmo-c { width: 1800px; height: 1800px; left: 40px; top: 100px;
          background: radial-gradient(circle, #ff5d8f 0%, rgba(255,93,143,0) 62%); }
\`\`\`

## Timeline code

Drift each orb on a slow, finite, desynchronised yoyo, and breathe its scale on a different period so the field never pulses in lockstep:

\`\`\`js
const tl = gsap.timeline({ paused: true });

function drift(target, startT, endT, dx, dy, period) {
    const halfCycles = Math.max(2, Math.round((endT - startT) / (period / 2)));
    tl.to(target, { x: dx, y: dy, duration: period / 2, ease: 'sine.inOut',
        yoyo: true, repeat: halfCycles - 1 }, startT);
}
drift('#atmo-a', 0, 10,  90, 60, 6.5);
drift('#atmo-b', 0, 10, -70, 50, 8.0);
drift('#atmo-c', 0, 10,  50, -80, 7.2);

gsap.set('#atmo-b', { transformOrigin: '50% 50%' });
tl.to('#atmo-b', { scale: 1.08, duration: 4, ease: 'sine.inOut', yoyo: true, repeat: 1 }, 0);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Glow size** is the gradient's transparent stop, not the box: pull the stop in (\`45%\`) for a tight spotlight, push it out (\`70%+\`) for a broad wash. The box always stays canvas-covering and the fade does the shaping.
- Two orbs for a calm, corporate feel; three to four for energy. More than four muddies into grey.
- Pull orb colours from the brand palette: the primary orb should carry the accent token (\`var(--l5-color-1, ...)\`); a second can use the muted-surface token. A third, purely decorative hue can be a literal hex — it never sits behind text, so it doesn't need a semantic token.
- Drift distance 50–110px over 6–9s periods reads as "weather", not movement. Larger/faster reads as a screensaver.
- For a darker, moodier base, lower orb opacity to ~0.35 and let the page background show through.

## Pitfalls

- **Contrast first.** Orbs sitting under text can wreck legibility — check WCAG AA contrast (4.5:1 body text, 3:1 large text) after placing them, and keep the brightest part of an orb away from body copy (or drop a flat scrim div between the field and the text).
- These tweens animate \`transform\` via GSAP, so the orbs must have **no \`transform\` in their CSS** (the usual rule). \`gsap.set(..., { transformOrigin })\` is fine — it's not a transform channel.
- **Finite repeats only** — size the yoyo from the on-screen window, never an infinite repeat.
- Keep orbs at \`z-index: 0\` (or first in source order) so they never sit over content; pair with the page's own base gradient rather than replacing it.
- Big blurs are the one genuinely GPU-heavy thing here. Three orbs is comfortable; a dozen will cost render time for no visible gain.`,
};
